import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { phoneValidator } from '@virtual-care/isomorphic';
import { Button, DialogFooter, Input, Label } from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';

import { CreateDialog } from '../../components/create-dialog.tsx';
import { GeneratedPassword } from '../../components/generated-password.tsx';
import { PhoneField } from '../../components/phone-field.tsx';

const schema = z.object({
  name: z
    .string()
    .min(1, { message: 'Name is required' })
    .regex(/^[a-zA-Z\s]*$/, {
      message: 'Name can only contain letters and spaces',
    }),
  phoneNumber: z.string(),
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
});
const phoneNumberValidator = schema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

export function CreateB2BPatient(props: {
  trigger: React.ReactNode;
  onSave?: () => void;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );

  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          const result = await client.request('POST /patients/b2b', {
            name: value.name,
            email: value.email,
            phoneNumber: value.phoneNumber,
          });
          setGeneratedPassword(result.password);
          toast.success('Enterprise account created and credentials emailed.');
          return void 0;
        } catch (error) {
          toast.error('Failed to create enterprise account.');
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={props.open}
      onOpenChange={(open) => {
        props.onOpenChange(open);
        if (!open) {
          setGeneratedPassword(null);
        }
      }}
      trigger={props.trigger}
      title="Create B2B Patient"
      subtitle="Generate a B2B account"
      onSave={props.onSave}
    >
      <form.Field
        name="name"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => {
                const value = event.target.value;
                const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
                field.handleChange(filteredValue);
              }}
              placeholder="Acme Corporation"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              placeholder="contact@acme.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="phoneNumber"
        validators={{
          onBlurAsync: phoneNumberValidator,
        }}
        children={(field) => <PhoneField field={field} />}
      />

      {generatedPassword && (
        <GeneratedPassword generatedPassword={generatedPassword} />
      )}

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />

      <DialogFooter>
        <form.Subscribe
          selector={(state) => [state.isSubmitSuccessful]}
          children={([isSubmitSuccessful]) =>
            isSubmitSuccessful ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  props.onOpenChange(false);
                  setGeneratedPassword(null);
                  form.reset();
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange(false)}
              >
                Cancel
              </Button>
            )
          }
        />
        <form.Subscribe
          selector={(state) =>
            [
              state.canSubmit,
              state.isSubmitting,
              state.isSubmitSuccessful,
            ] as const
          }
          children={([canSubmit, isSubmitting, isSubmitSuccessful]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting || isSubmitSuccessful}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
